// Dimension config — single source of truth on the React side for: label,
// formatter, good-direction (which sign of delta is positive for the user),
// the summary-stat key the engine output uses, and the caveat-footer copy.
//
// The R-side counterpart lives in app/logic/engine.R; if the engine adds
// or removes a dimension, update here at the same time.

import type { SummaryStats } from "./types";
import {
  formatEur,
  formatEurAbbreviated,
  formatPercent,
  formatRatio,
  type Formatter,
} from "./formatters";

export type { Formatter };

export type DimensionKey =
  | "household_income"
  | "inequality"
  | "poverty"
  | "redistribution"
  | "state_budget"
  | "tax_burden";

export interface Dimension {
  key: DimensionKey;
  /** Short title shown in the dimension's box/card (e.g. "Inequality
   *  (Gini)") — names the number the box displays. */
  label: string;
  /** Optional longer title shown as the heading once the card is expanded
   *  (e.g. "Inequality", "Redistribution and progressivity"). Falls back
   *  to `label` when unset. Decoupled because the box names the headline
   *  number while the expanded page may cover more than that one number
   *  (#256). */
  pageTitle?: string;
  /** A one-sentence definition of what the dimension measures. Reserved
   *  for a future definition-above-chart slot; currently unused by the
   *  expanded card body. */
  description: string;
  /** The caveat-footer line rendered below the chart in the expanded
   *  card body. Shared across all dimensions; ChartCaveat appends the
   *  disclaimer link. */
  caveat: string;
  formatter: Formatter;
  /** Whether positive change is "good for the user" — drives delta colour. */
  goodWhen: "increasing" | "decreasing";
}

// One caveat for every chart. The Brussels-Capital-Region clause is on
// every footer so even the federal instruments read as BCR-population
// impacts (#255). The "~2,000 sample" figure is the current CVAE
// synthetic dataset; ChartCaveat appends the disclaimer link.
const STANDARD_CAVEAT =
  "Static analysis. Synthetic households, ~2,000 sample, representing the Brussels Capital Region population.";

export const DIMENSIONS: readonly Dimension[] = [
  {
    key: "household_income",
    label: "Household income",
    description:
      "Median monthly household income after all taxes — including VAT and excise — adjusted for household size (equivalised). Half of people are in a household with more, half with less.",
    caveat: STANDARD_CAVEAT,
    formatter: formatEur,
    goodWhen: "increasing",
  },
  {
    key: "inequality",
    label: "Inequality (Gini)",
    pageTitle: "Inequality",
    description:
      "Gini coefficient of equivalised household income after all taxes (incl. VAT and excise). 0 = perfectly equal; 1 = one household has everything. Lower is more equal.",
    caveat: STANDARD_CAVEAT,
    formatter: formatRatio,
    goodWhen: "decreasing",
  },
  {
    key: "poverty",
    label: "Poverty rate",
    description:
      "Share of people below 60% of the median equivalised income — the EU at-risk-of-poverty line. The line is relative, so it moves with the reform (a reform that shifts everyone leaves the rate unchanged).",
    caveat: STANDARD_CAVEAT,
    formatter: formatPercent,
    goodWhen: "decreasing",
  },
  {
    key: "redistribution",
    label: "Redistribution",
    pageTitle: "Redistribution and progressivity",
    description:
      "Inequality reduction achieved by the tax-benefit system. Pre-tax Gini minus post-tax Gini. Higher means the system redistributes more.",
    caveat: STANDARD_CAVEAT,
    formatter: formatRatio,
    goodWhen: "increasing",
  },
  {
    key: "state_budget",
    label: "Public budget impact",
    description:
      "Net flow to the public budget for the year, across all levels (federal, regional, community). Positive = revenue raised; negative = cost to the state.",
    caveat: STANDARD_CAVEAT,
    formatter: formatEurAbbreviated,
    goodWhen: "increasing",
  },
  {
    key: "tax_burden",
    label: "Tax burden",
    description:
      "Average share of pre-tax income paid in taxes across all households. Includes consumption taxes (VAT, excise), which are paid out of take-home income.",
    caveat: STANDARD_CAVEAT,
    formatter: formatPercent,
    goodWhen: "decreasing",
  },
] as const;

export const dimensionByKey: Record<DimensionKey, Dimension> = Object.fromEntries(
  DIMENSIONS.map((d) => [d.key, d]),
) as Record<DimensionKey, Dimension>;

// Semantic gain/loss colours come in two tiers, mirroring the token SSOT
// ($beamm-gain / $beamm-loss and their *-text variants in _tokens.scss):
//
//   FILL tier  — chart areas / bars / SVG marks. Brand-weighted, judged at the
//                3:1 graphical-object threshold. NOT for body text.
//   TEXT tier  — gain/loss rendered as small text or labels. AA body (4.5:1).
//
// RULE: any gain/loss shown as text uses the *_TEXT_COLOUR tier; the *_COLOUR
// fills are for chart areas only. Use deltaTextColour() for delta labels.

/** FILL tier — chart areas only ($beamm-gain / $beamm-loss). */
export const GAIN_COLOUR = "#7fbf69";
export const LOSS_COLOUR = "#c66e5c";

/** TEXT tier — gain/loss text & labels, AA body ($beamm-gain-text / -loss-text). */
export const GAIN_TEXT_COLOUR = "#3f7e2c";
export const LOSS_TEXT_COLOUR = "#c44a3d";

/** Whether a delta is "good for the user" given the dimension's direction. */
export function isGoodDelta(delta: number, goodWhen: Dimension["goodWhen"]): boolean {
  if (delta === 0) return true;
  return (delta > 0 && goodWhen === "increasing") ||
    (delta < 0 && goodWhen === "decreasing");
}

/** Text-tier colour for a gain/loss delta label, by the dimension's good
 *  direction. Use this for any gain/loss rendered as text; the *_COLOUR
 *  fills are for chart areas only. */
export function deltaTextColour(
  delta: number,
  goodWhen: Dimension["goodWhen"],
): string {
  return isGoodDelta(delta, goodWhen) ? GAIN_TEXT_COLOUR : LOSS_TEXT_COLOUR;
}

/** Read a single summary-stat value from an EngineOutput. */
export function statFor(
  stats: SummaryStats,
  key: DimensionKey,
): number {
  return stats[key];
}
